import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Radar, Crosshair, Trophy, Library, BarChart3, LogIn, LogOut, User, Shield, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isMuted, toggleMute, onMuteChange, play as sfx, playHover } from "@/utils/sounds";

const navItems = [
  { to: "/", label: "Briefing", icon: Radar, testId: "nav-briefing" },
  { to: "/practice", label: "Drill", icon: Crosshair, testId: "nav-practice" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testId: "nav-leaderboard" },
  { to: "/library", label: "Stratagems", icon: Library, testId: "nav-library" },
  { to: "/stats", label: "War Log", icon: BarChart3, testId: "nav-stats" },
];

const NavBar = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [muted, setMuted] = useState(isMuted());

  useEffect(() => onMuteChange(setMuted), []);

  return (
    <div className="w-full border-b border-white/10 bg-black/50 backdrop-blur-md">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-6 px-6 py-5 md:px-12">
        <div className="flex items-center gap-4">
          <div
            className="h-10 w-10 rounded-sm border border-[#FFE800]/60 bg-black/60 flex items-center justify-center font-headings text-lg text-[#FFE800] glow-yellow"
            data-testid="nav-badge"
          >
            SST
          </div>
          <div>
            <p
              className="font-headings text-2xl tracking-widest text-white"
              data-testid="nav-brand"
            >
              Super Stratagem Trainer
            </p>
            <p
              className="text-xs text-gray-400 uppercase tracking-[0.3em]"
              data-testid="nav-subtitle"
            >
              Super Earth Training Console
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <nav className="flex flex-wrap items-center gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                onClick={() => sfx("click")}
                onMouseEnter={playHover}
                className={({ isActive }) =>
                  `flex items-center gap-2 border border-white/10 px-4 py-2 font-headings text-lg uppercase tracking-[0.2em] transition-all duration-200 ${
                    isActive
                      ? "text-[#FFE800] border-[#FFE800]/70 bg-black/70"
                      : "text-gray-300 hover:text-[#FFE800] hover:border-[#FFE800]/40"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <button
            onClick={() => toggleMute()}
            className="border border-white/10 px-2 py-2 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40 transition-colors duration-150"
            data-testid="nav-mute-toggle"
            title={muted ? "Unmute sounds" : "Mute sounds"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>

          {!loading && (
            user ? (
              <div className="flex items-center gap-3" data-testid="nav-user-section">
                {["admin", "master_admin"].includes(user.role) && (
                  <NavLink
                    to="/admin"
                    onClick={() => sfx("click")}
                    onMouseEnter={playHover}
                    className={({ isActive }) =>
                      `flex items-center gap-1 border px-3 py-1 text-xs font-headings uppercase tracking-widest ${
                        isActive
                          ? "border-red-500 text-red-400"
                          : "border-red-500/30 text-red-400/60 hover:text-red-400 hover:border-red-500"
                      }`
                    }
                    data-testid="nav-admin"
                  >
                    <Shield className="h-3 w-3" /> Admin
                  </NavLink>
                )}
                <div
                  className="flex items-center gap-2 border border-[#FFE800]/40 px-3 py-1"
                  data-testid="nav-user-pill"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="h-5 w-5 rounded-full"
                    />
                  ) : (
                    <User className="h-4 w-4 text-[#FFE800]" />
                  )}
                  <span className="text-xs font-data text-[#FFE800]" data-testid="nav-callsign">
                    {user.callsign || user.name || "Helldiver"}
                  </span>
                </div>
                <button
                  onClick={async () => { sfx("back"); await logout(); navigate("/"); }}
                  onMouseEnter={playHover}
                  className="border border-white/10 px-3 py-1 text-xs font-data text-gray-400 hover:text-red-400 hover:border-red-400/40"
                  data-testid="nav-logout"
                >
                  <LogOut className="h-3 w-3 inline mr-1" />
                  Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => { sfx("click"); navigate("/login"); }}
                onMouseEnter={playHover}
                className="flex items-center gap-2 border border-[#FFE800]/40 px-4 py-2 text-xs font-headings uppercase tracking-widest text-[#FFE800] hover:border-[#FFE800] hover:bg-[#FFE800]/10"
                data-testid="nav-login"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default NavBar;
